/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import NumberFormat from '../../../utils/number-format';
import Dashboard, { DashboardColumn, DashboardRow, DashboardSection } from '../../dashboard';
import Loader from '../../loader';
import Table from '../../table/Table';
import Error from '../../error';
import SettingsSidebar, { SearchBox, SettingsSection } from '../../settings-sidebar';
import { escapeRegexCharacters } from '../../../utils/string-format';
import { getFrameworkName, getStandardName, convertToDateParams } from '../utils';
import { naturalSort } from '../../../utils';
import { ReportTypes } from '../../../types/statsTypes';
import { useReportData } from '../hooks/useReportData';
import { StaffFilters } from '../partials/StaffFilters';
import { useStaffFilters } from '../hooks/useStaffFilters';
import { QUESTION } from '../../../constants/terminology';

const columns = [
  {
    Header: 'Standard',
    accessor: 'name',
    cellProps: {
      className: 'text-wrap',
    },
  },
  {
    Header: 'In Scope',
    accessor: 'count',
    cellProps: {
      className: 'text-right pr-2',
    },
    Cell: (a: any) => <NumberFormat value={a.row.original.count} />
  },
  {
    Header: 'Answered %',
    accessor: 'answered',
    cellProps: {
      className: 'text-right pr-2',
    },
    sortType: (a: any, b: any) => naturalSort(a.original.answeredPc, b.original.answeredPc),
    Cell: (a: any) => <NumberFormat value={a.row.original.answeredPc} decimalPlaces={1} suffix={'%'} />
  },
  {
    Header: 'Verified %',
    accessor: 'verifiedPc',
    cellProps: {
      className: 'text-right pr-2',
    },
    sortType: (a: any, b: any) => naturalSort(a.original.verifiedPc, b.original.verifiedPc),
    Cell: (a: any) => <NumberFormat value={a.row.original.verifiedPc} decimalPlaces={1} suffix={'%'} />
  },
];

interface StatsData {
  _id: string;
  name: string;
  status: string;
  count: number;
  tags?: {
    [key: string]: string[]
  };
  alternatives?: {
    [key: string]: {
      typeCode: string;
    }
  }
}

interface StatsRow {
  name?: string;
  count: number;
  verified: number;
  answered: number;
  verifiedPc: number;
  answeredPc: number;
}

const Standards = () => {
  const [searchText, setSearchText] = React.useState('');
  const [hasError, setHasError] = React.useState(false);
  const [rows, setRows] = React.useState<StatsData[] | undefined>();

  const staffFilterProps = useStaffFilters();
  const { extraFilterApiParams, ...staffFilters } = staffFilterProps;

  const successCallback = React.useCallback((data?: StatsData[]) => {
    setHasError(false);
    setRows(data);
  }, []);

  const errorCallback = React.useCallback(() => {
    setHasError(true)
  }, []);

  useReportData({
    reportType: ReportTypes.Standard,
    successCallback,
    errorCallback,
    extraProps: extraFilterApiParams,
    dateParams: convertToDateParams(staffFilterProps.dateRange),
  });

  const filteredRows = React.useMemo(() => {
    if (!rows) {
      return [];
    }

    const filterScore = (row: StatsRow) => {
      if (searchText) {
        const escapedSearchText = escapeRegexCharacters(searchText.trim().toLowerCase());
        const searchTextRegex = new RegExp(escapedSearchText.replace(/ /g, '|'), 'g');
        const searchString = `${row.name}`.toLowerCase();
        return (searchString.match(searchTextRegex) || []).length;
      }

      return 1;
    }

    const filtered: {
      [key: string]: StatsRow;
    } = {};

    const addRow = (tag: string, r: StatsData) => {
      if (!filtered[tag]) {
        filtered[tag] = {
          name: tag,
          count: 0,
          verified: 0,
          answered: 0,
          answeredPc: 0,
          verifiedPc: 0,
        }
      };

      filtered[tag].count += r.count;
      if (r.status === 'verified') {
        filtered[tag].verified += r.count;
      }
      if (r.status !== 'created') {
        filtered[tag].answered += r.count;
      }
      filtered[tag].answeredPc = 100 * filtered[tag].answered / filtered[tag].count;
      filtered[tag].verifiedPc = 100 * filtered[tag].verified / filtered[tag].count;
    }

    rows.forEach(r => {
      if (r.tags) {
        for (const [tag, tags] of Object.entries(r.tags)) {
          tags.forEach(t => addRow(getFrameworkName(tag, t), r));
        }
      }
      if (r.alternatives) {
        for (const [tag, alternative] of Object.entries(r.alternatives)) {
          addRow(getStandardName(tag, alternative.typeCode), r);
        }
      }
    });

    return Object.values(filtered).filter(filterScore);
  }, [rows, searchText]);

  if (hasError) {
    return <Error />;
  }

  const total = {
    name: '',
    unique: 0,
    count: 0,
    verified: 0,
    answered: 0
  };

  filteredRows.forEach(r => {
    total.unique++;
    total.count += r.count;
    total.verified += r.verified;
    total.answered += r.answered;
  });

  const isLoading = rows === undefined;

  return (
    <Dashboard hasSidebar>
      <SettingsSidebar>
        <SettingsSection
          title={'Search Standards or Frameworks'}
          subtitle='Search for specific standards or frameworks by name.'
          icon='fa-search'
          buttons={[
            {
              icon: 'fa-times-circle',
              tooltip: 'Clear search',
              onClick: () => setSearchText(''),
            }
          ]}>
          <SearchBox
            handleOnChange={(e) => setSearchText(String((e.target as HTMLInputElement).value))}
            value={searchText}
          />
        </SettingsSection>
        <StaffFilters {...staffFilters} reportType={ReportTypes.Standard} />
      </SettingsSidebar>
      <DashboardRow>
        <DashboardColumn
          title='Total Groups'
          flexBasisPc={`${100/3}%`}
        >
          <div className='text-right'>
            <NumberFormat value={total.unique} />
          </div>
        </DashboardColumn>
        <DashboardColumn
          title={`Answered ${QUESTION.CAPITALIZED_PLURAL}`}
          flexBasisPc={`${100/3}%`}
        >
          <div className='text-right'>
            <NumberFormat value={100 * total.answered / total.count} decimalPlaces={1} suffix={'%'} />
          </div>
        </DashboardColumn>
        <DashboardColumn
          title={`Verified ${QUESTION.CAPITALIZED_PLURAL}`}
          flexBasisPc={`${100/3}%`}
        >
          <div className='text-right'>
            <NumberFormat value={100 * total.verified / total.count} decimalPlaces={1} suffix={'%'} />
          </div>
        </DashboardColumn>
      </DashboardRow>
      <DashboardSection
        title='Standards and Frameworks Statistics'
        icon='fa-clipboard-list'
      >
        {isLoading
          ?
          <Loader />
          :
          filteredRows.length === 0
            ?
            <>No results</>
            :
            <Table
              showRowCount={true}
              columns={columns}
              data={filteredRows}
            />
        }
      </DashboardSection>
    </Dashboard>
  );
}

export default Standards;
